import { TiktokAdGroupLocationOption } from '@/app/common/general-types/tiktok-spark-ads.dto';

/** A market the user can pick, flattened out of TikTok's nested region tree. */
export interface SparkMarketOption {
  location_id: string;
  name: string;
}

/**
 * Flattens TikTok's nested region tree into a pickable list.
 *
 * `tool/region/` nests sub-regions under each country; at `TO_COUNTRY` the tree is
 * usually one level deep, but the recursion keeps this correct for wider level ranges.
 * Nodes without both an id and a name are dropped rather than rendered as bare ids,
 * and duplicates are collapsed since a region can appear at more than one depth.
 */
export function flattenRegions(
  regions: readonly TiktokAdGroupLocationOption[] | undefined,
): SparkMarketOption[] {
  const seen = new Set<string>();
  const flat: SparkMarketOption[] = [];

  const walk = (nodes: readonly TiktokAdGroupLocationOption[]): void => {
    for (const node of nodes) {
      if (node?.location_id && node.name && !seen.has(node.location_id)) {
        seen.add(node.location_id);
        flat.push({ location_id: node.location_id, name: node.name });
      }
      if (node?.sub_regions?.length) {
        walk(node.sub_regions);
      }
    }
  };

  walk(regions ?? []);
  return flat;
}

/** Markets not already chosen, alphabetical — what the picker offers. */
export function addableMarkets(
  regions: readonly TiktokAdGroupLocationOption[] | undefined,
  selectedLocationIds: readonly string[],
): SparkMarketOption[] {
  const selected = new Set(selectedLocationIds);
  return flattenRegions(regions)
    .filter((market) => !selected.has(market.location_id))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** TikTok schedules want `YYYY-MM-DD HH:mm:ss`; `datetime-local` gives `T`-separated. */
export function toTikTokTime(value: string | undefined): string | undefined {
  if (!value) {
    return undefined;
  }
  const normalised = value.replace('T', ' ');
  return normalised.length === 16 ? `${normalised}:00` : normalised;
}
